import { getSettingsOptions } from 'lib/reader';

import type { ServerConfig } from 'shared-types';
import type { ReaderSettingsFontSize, ReaderSettingsView } from 'shared-types';

interface Params {
  serverConfig: ServerConfig<'guest'>;
  useSavedSettings?: boolean;
}

interface Settings {
  chatNotification: boolean;
  font: string;
  fontSize: ReaderSettingsFontSize;
  readerWidth: number;
  showToc: boolean;
  theme: string | number;
  view: ReaderSettingsView;
}

interface InitialSettings {
  settings: Settings;
  settingsPresentMode: Settings;
}

const getInitialSettings = ({
  serverConfig,
  useSavedSettings = true
}: Params): InitialSettings => {
  const mergeSettings = <T>({ defaultData, savedData, options }): T => {
    const merged = { ...defaultData };
    Object.keys(savedData).forEach((key) => {
      if (merged.hasOwnProperty(key)) {
        // in old version values were stored as objects, ex. {value: true},
        // but now we store values directly
        const savedValue =
          savedData[key].value !== undefined
            ? savedData[key].value
            : savedData[key];
        // validate value
        if (
          !options[key] ||
          options[key].find((option) => option.value === savedValue)
        ) {
          if (key === 'readerWidth') {
            merged[key] = parseInt(savedValue, 10) || defaultData.readerWidth;
          } else if (key === 'theme' && serverConfig.readerCustomTheme?.id) {
            merged[key] = serverConfig.readerCustomTheme.id;
          } else if (key === 'view' && !options[key]) {
            merged[key] = 'continuous' as ReaderSettingsView;
          } else {
            merged[key] = savedValue;
          }
        }
      }
    });
    return merged;
  };

  const defaultSettings = {
    chatNotification: serverConfig.readerEnableChat,
    font:
      serverConfig.readerCustomTheme?.customFontValue ||
      serverConfig.readerDefaultFont,
    fontSize: 'medium',
    readerWidth: serverConfig.readerDefaultChapterWidth,
    showToc: true,
    theme: serverConfig.readerCustomTheme?.id
      ? serverConfig.readerCustomTheme.id
      : serverConfig.readerDefaultTheme || 'blue',
    view: 'continuous' as ReaderSettingsView
  };

  const defaultSettingsPresentMode: Settings = {
    ...defaultSettings,
    fontSize: 'large',
    readerWidth: 1920,
    view: 'pages' as ReaderSettingsView
  };

  if (!useSavedSettings) {
    return {
      settings: defaultSettings as Settings,
      settingsPresentMode: defaultSettingsPresentMode as Settings
    };
  }

  let savedSettings = {};
  let savedSettingsPresentMode = {};
  try {
    savedSettings = JSON.parse(localStorage.getItem('vuoSettings')) || {};
    savedSettingsPresentMode =
      JSON.parse(localStorage.getItem('vuoSettingsPresentMode')) || {};
  } catch (err) {
    console.error(err);
  }

  const options = getSettingsOptions({ serverConfig });

  const settings = mergeSettings<Settings>({
    defaultData: defaultSettings,
    savedData: savedSettings,
    options
  });

  const settingsPresentMode = mergeSettings<Settings>({
    defaultData: defaultSettingsPresentMode,
    savedData: savedSettingsPresentMode,
    options
  });

  return { settings, settingsPresentMode };
};

export default getInitialSettings;
